/**
 * Datasources — the user-facing record of a place an app reads and writes
 * (alert-live-datasources Phase 2; decided 2026-09-25: a new store that
 * WebDavSync and WebDavManager adopt later).
 *
 * A datasource DESCRIBES a mount; the mount (acequiaMounts) is what the
 * service worker acts on. Writing a datasource writes its mount, and its
 * credential when it has one, then tells the worker (MOUNTS_UPDATED).
 *
 *   {
 *     id,                 stable id; the mount key is `ds:<id>`
 *     app,                owning app ('alert.live') — lists are per app
 *     displayName,
 *     role,               'space' (events + layers) | 'assets' (uploads)
 *     kind,               'acequia'    an Acequia domain, chain short code
 *                         'webdav'     any WebDAV server, bearer token or none
 *                         'browserdav' a BrowserDAV share over the group proxy
 *                         'opfs'       a local folder in this browser (no server)
 *     mountpoint,         where it appears on this origin, e.g. /acq/alert.live-draft
 *     serverUrl, subPath  remote kinds
 *     opfsPath            opfs: a directory in the origin's OPFS, app-named
 *     createdAt, updatedAt
 *   }
 *
 * Credentials are never kept on the datasource record: a token goes to
 * `acequiaCredentials` under the mount's credentialsKey (scoped short codes
 * in IndexedDB are accepted — decision of 2026-09-28).
 */
import { localforage } from '../deps.js'
import { reservedMountpoint } from './swMounts.js'

export const ROLES = Object.freeze(['space', 'assets'])
export const KINDS = Object.freeze(['acequia', 'webdav', 'browserdav', 'opfs'])
const REMOTE = new Set(['acequia', 'webdav', 'browserdav'])

function normPath(p) {
    let s = String(p || '').trim()
    if (!s.startsWith('/')) s = '/' + s
    return s.length > 1 ? s.replace(/\/+$/, '') : s
}

export function slugify(s) {
    return String(s || '')
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 40)
}

/**
 * Check and normalize a datasource. Returns { ok: true, ds } or
 * { ok: false, error }. Pure.
 */
export function normalizeDatasource(input, { now = Date.now() } = {}) {
    const d = { ...(input || {}) }
    if (!d.id) return { ok: false, error: 'a datasource needs an id' }
    if (!d.app) return { ok: false, error: 'a datasource needs an app' }
    if (!ROLES.includes(d.role)) return { ok: false, error: `role must be one of ${ROLES.join(', ')}` }
    if (!KINDS.includes(d.kind)) return { ok: false, error: `kind must be one of ${KINDS.join(', ')}` }
    const mountpoint = normPath(d.mountpoint)
    // A mountpoint may not capture the page itself: never `/`, `/src`, `/auth`, …
    // The list is the worker's (swMounts.RESERVED_MOUNTPOINTS), which refuses
    // the same for a record written by any other route
    if (reservedMountpoint(mountpoint)) {
        return { ok: false, error: `mountpoint ${mountpoint} would shadow the app itself` }
    }
    if (mountpoint.split('/').filter(Boolean).length < 1) return { ok: false, error: 'mountpoint needs at least one segment' }
    const out = {
        id: String(d.id),
        app: String(d.app),
        displayName: String(d.displayName || d.id).trim(),
        role: d.role,
        kind: d.kind,
        mountpoint,
        createdAt: d.createdAt || now,
        updatedAt: now,
    }
    if (REMOTE.has(d.kind)) {
        let url
        try {
            url = new URL(String(d.serverUrl || ''))
        } catch {
            return { ok: false, error: 'a remote datasource needs a server URL' }
        }
        if (!/^https?:$/.test(url.protocol)) return { ok: false, error: 'the server URL must be http(s)' }
        out.serverUrl = url.href.replace(/\/+$/, '')
        const sub = d.subPath ? normPath(d.subPath) : ''
        out.subPath = sub === '/' ? '' : sub
    } else {
        const opfsPath = normPath(d.opfsPath || '').replace(/^\/+/, '')
        if (!opfsPath) return { ok: false, error: 'a local datasource needs a folder name (never the OPFS root)' }
        out.opfsPath = opfsPath
    }
    return { ok: true, ds: Object.freeze(out) }
}

export function mountKeyFor(ds) {
    return `ds:${ds.id}`
}

/** The acequiaMounts record a datasource writes. Pure. */
export function mountRecordFor(ds) {
    if (ds.kind === 'opfs') return { mountpoint: ds.mountpoint, source: 'opfs', path: ds.opfsPath }
    return { mountpoint: ds.mountpoint, serverUrl: ds.serverUrl, subPath: ds.subPath || '', credentialsKey: mountKeyFor(ds) }
}

/**
 * The acequiaCredentials record for a remote datasource, or null for a local
 * one. An Acequia domain takes a chain short code (redeemed by the worker);
 * any other WebDAV server takes its token verbatim (`bearer`). No token: the
 * worker presents a self-minted user token, which a server with auth off
 * ignores — the same convention as alert.live's test-server override.
 */
export function credentialRecordFor(ds, token) {
    if (ds.kind === 'opfs') return null
    const t = typeof token === 'string' && token.trim() ? token.trim() : null
    if (ds.kind === 'acequia' || !t) return { type: 'acequiaToken', token: t, serverUrl: ds.serverUrl }
    return { type: 'bearer', token: t, serverUrl: ds.serverUrl }
}

/** A mountpoint for a new datasource from its name, under the app's /acq prefix. */
export function suggestMountpoint(app, displayName, taken = []) {
    const base = `/acq/${slugify(app) || 'app'}-${slugify(displayName) || 'source'}`
    let mp = base
    for (let i = 2; taken.includes(mp); i++) mp = `${base}-${i}`
    return mp
}

/**
 * The store. `app` scopes list(); everything shares one IndexedDB store so a
 * file manager can show every app's sources. Changes broadcast on a
 * BroadcastChannel so other tabs refresh.
 */
export function createDatasourceStore({
    app,
    lf = localforage,
    notifyWorker = (msg) => globalThis.navigator?.serviceWorker?.controller?.postMessage(msg),
    channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('acequia-datasources') : null,
} = {}) {
    const records = lf.createInstance({ name: 'acequiaDatasources' })
    const mounts = lf.createInstance({ name: 'acequiaMounts' })
    const credentials = lf.createInstance({ name: 'acequiaCredentials' })
    const listeners = new Set()
    const announce = (id) => {
        notifyWorker({ type: 'MOUNTS_UPDATED' })
        channel?.postMessage({ type: 'datasources-changed', app, id })
        for (const cb of listeners) cb({ app, id })
    }
    channel?.addEventListener?.('message', (e) => {
        if (e.data?.type === 'datasources-changed') for (const cb of listeners) cb(e.data)
    })

    return {
        app,
        async list({ all = false } = {}) {
            const out = []
            for (const key of await records.keys()) {
                const r = await records.getItem(key)
                if (r && (all || r.app === app)) out.push(r)
            }
            return out.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0))
        },
        async get(id) {
            return (await records.getItem(id)) || null
        },
        /** Validate, write the record, its mount and credential, tell the worker. */
        async put(input, { token } = {}) {
            const existing = input?.id ? await records.getItem(input.id) : null
            const n = normalizeDatasource({ app, ...existing, ...input, createdAt: existing?.createdAt })
            if (!n.ok) throw new Error(n.error)
            const ds = n.ds
            for (const other of await this.list({ all: true })) {
                if (other.id !== ds.id && other.mountpoint === ds.mountpoint) {
                    throw new Error(`${ds.mountpoint} is already used by “${other.displayName}”`)
                }
            }
            await records.setItem(ds.id, ds)
            await mounts.setItem(mountKeyFor(ds), mountRecordFor(ds))
            const cred = credentialRecordFor(ds, token)
            if (cred && (token !== undefined || !(await credentials.getItem(mountKeyFor(ds))))) {
                await credentials.setItem(mountKeyFor(ds), cred)
            }
            announce(ds.id)
            return ds
        },
        /** Remove the record, its mount and credential. The data it pointed at is untouched. */
        async remove(id) {
            const ds = await records.getItem(id)
            await records.removeItem(id)
            if (ds) {
                await mounts.removeItem(mountKeyFor(ds))
                await credentials.removeItem(mountKeyFor(ds))
            }
            announce(id)
            return ds
        },
        onChange(cb) {
            listeners.add(cb)
            return () => listeners.delete(cb)
        },
    }
}
